/**
 * Signalverarbeitung fuer die Song-Analyse – reine Funktionen, ohne DOM,
 * laufen im Web Worker und in den Tests gleich.
 *
 * Alles arbeitet auf Mono-Audio mit 22 050 Hz (so erwartet es auch Basic
 * Pitch). Das Audio bleibt im Arbeitsspeicher; nichts hier speichert oder
 * sendet etwas (docs/SONG-ANALYSE.md).
 */

export const SAMPLE_RATE = 22_050;
/** Schrittweite der Merkmale: 256 Samples ≈ 11,6 ms (≈ 86 Frames/s, wie Basic Pitch). */
export const HOP = 256;
export const FPS = SAMPLE_RATE / HOP;

/** Radix-2-FFT in place (re, im), Laenge Zweierpotenz. */
export function fft(re: Float32Array, im: Float32Array): void {
  const n = re.length;
  for (let i = 1, j = 0; i < n; i++) {
    let bit = n >> 1;
    for (; j & bit; bit >>= 1) j ^= bit;
    j ^= bit;
    if (i < j) {
      [re[i], re[j]] = [re[j] as number, re[i] as number];
      [im[i], im[j]] = [im[j] as number, im[i] as number];
    }
  }
  for (let len = 2; len <= n; len <<= 1) {
    const ang = (-2 * Math.PI) / len;
    const wr = Math.cos(ang);
    const wi = Math.sin(ang);
    for (let i = 0; i < n; i += len) {
      let cr = 1;
      let ci = 0;
      for (let k = 0; k < len / 2; k++) {
        const a = i + k;
        const b = a + len / 2;
        const tr = (re[b] as number) * cr - (im[b] as number) * ci;
        const ti = (re[b] as number) * ci + (im[b] as number) * cr;
        re[b] = (re[a] as number) - tr;
        im[b] = (im[a] as number) - ti;
        re[a] = (re[a] as number) + tr;
        im[a] = (im[a] as number) + ti;
        const nr = cr * wr - ci * wi;
        ci = cr * wi + ci * wr;
        cr = nr;
      }
    }
  }
}

/**
 * Onset-Staerke (spektraler Fluss auf log-komprimiertem Betragsspektrum,
 * in ~40 logarithmischen Baendern) – ein Wert je Frame (HOP), 0..1.
 * Grundlage fuer Tempo und Beats.
 */
export function onsetEnvelope(pcm: Float32Array, nFft = 1024): Float32Array {
  const frames = Math.max(1, Math.floor(pcm.length / HOP));
  const window = new Float32Array(nFft).map((_, i) => 0.5 - 0.5 * Math.cos((2 * Math.PI * i) / nFft));
  // Baender: logarithmisch von 30 Hz bis 8 kHz
  const bands = 40;
  const edges: number[] = [];
  for (let b = 0; b <= bands; b++) edges.push(Math.round(((30 * Math.pow(8000 / 30, b / bands)) / SAMPLE_RATE) * nFft));
  const re = new Float32Array(nFft);
  const im = new Float32Array(nFft);
  let prev = new Float32Array(bands);
  const out = new Float32Array(frames);
  for (let f = 0; f < frames; f++) {
    const start = f * HOP - nFft / 2;
    for (let i = 0; i < nFft; i++) {
      const s = start + i;
      re[i] = s >= 0 && s < pcm.length ? (pcm[s] as number) * (window[i] as number) : 0;
      im[i] = 0;
    }
    fft(re, im);
    const cur = new Float32Array(bands);
    for (let b = 0; b < bands; b++) {
      let sum = 0;
      const lo = edges[b] as number;
      const hi = Math.max(lo + 1, edges[b + 1] as number);
      for (let k = lo; k < hi; k++) sum += Math.hypot(re[k] as number, im[k] as number);
      cur[b] = Math.log1p(100 * (sum / (hi - lo)));
    }
    let flux = 0;
    for (let b = 0; b < bands; b++) flux += Math.max(0, (cur[b] as number) - (prev[b] as number));
    out[f] = flux;
    prev = cur;
  }
  // Langsamen Anteil abziehen (gleitender Mittelwert ~0,5 s), dann normieren.
  const w = Math.round(FPS * 0.25);
  const smooth = movingAverage(out, w);
  let max = 1e-9;
  for (let i = 0; i < frames; i++) {
    out[i] = Math.max(0, (out[i] as number) - (smooth[i] as number));
    if ((out[i] as number) > max) max = out[i] as number;
  }
  for (let i = 0; i < frames; i++) out[i] = (out[i] as number) / max;
  return out;
}

export function movingAverage(x: Float32Array, half: number): Float32Array {
  const out = new Float32Array(x.length);
  let sum = 0;
  let lo = 0;
  let hi = -1;
  for (let i = 0; i < x.length; i++) {
    const a = Math.max(0, i - half);
    const b = Math.min(x.length - 1, i + half);
    while (hi < b) sum += x[++hi] as number;
    while (lo < a) sum -= x[lo++] as number;
    out[i] = sum / (b - a + 1);
  }
  return out;
}

/** Lautheit (RMS) je Frame. */
export function rmsEnvelope(pcm: Float32Array): Float32Array {
  const frames = Math.max(1, Math.floor(pcm.length / HOP));
  const out = new Float32Array(frames);
  for (let f = 0; f < frames; f++) {
    let sum = 0;
    for (let i = f * HOP; i < Math.min(pcm.length, (f + 2) * HOP); i++) sum += (pcm[i] as number) ** 2;
    out[f] = Math.sqrt(sum / (2 * HOP));
  }
  return out;
}
